import { FC } from 'react';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import {
  EditorialFacebookPostNetwork,
  EditorialFacebookPostType,
} from '@/utils/enums';

const Heading = styled(Text)`
  margin: 0 0 24px;
  text-align: center;
`;

const Dialog = styled(StyledDialog)`
  width: 464px;
  padding: 56px 24px 24px;
`;

const Body = styled.div`
  display: grid;
  flex-direction: column;
  gap: 8px;
`;

const Gap = styled.div`
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  flex-direction: column;
`;

const NetworkLabel = styled.span`
  text-transform: capitalize;
`;

export const postTypes = [
  {
    label: 'Link',
    type: EditorialFacebookPostType.LINK,
    supportedNetworks: [
      EditorialFacebookPostNetwork.FACEBOOK,
      EditorialFacebookPostNetwork.TWITTER,
      EditorialFacebookPostNetwork.WHATSAPP,
      EditorialFacebookPostNetwork.BLUESKY,
      EditorialFacebookPostNetwork.LINKEDIN,
      EditorialFacebookPostNetwork.REDDIT,
    ],
  },
  {
    label: 'Share',
    type: EditorialFacebookPostType.SHARE,
    supportedNetworks: [EditorialFacebookPostNetwork.FACEBOOK],
  },
  {
    label: 'Image',
    type: EditorialFacebookPostType.IMAGE,
    supportedNetworks: [
      EditorialFacebookPostNetwork.FACEBOOK,
      EditorialFacebookPostNetwork.INSTAGRAM,
      EditorialFacebookPostNetwork.PINTEREST,
      EditorialFacebookPostNetwork.THREADS,
      EditorialFacebookPostNetwork.TWITTER,
      EditorialFacebookPostNetwork.WHATSAPP,
      EditorialFacebookPostNetwork.BLUESKY,
      EditorialFacebookPostNetwork.LINKEDIN,
      EditorialFacebookPostNetwork.REDDIT,
    ],
  },
  {
    label: 'Reel',
    type: EditorialFacebookPostType.REEL,
    supportedNetworks: [
      EditorialFacebookPostNetwork.FACEBOOK,
      EditorialFacebookPostNetwork.INSTAGRAM,
      EditorialFacebookPostNetwork.REELS,
      EditorialFacebookPostNetwork.YOUTUBE_SHORTS,
    ],
  },
  {
    label: 'Video',
    type: EditorialFacebookPostType.VIDEO,
    supportedNetworks: [
      EditorialFacebookPostNetwork.FACEBOOK,
      EditorialFacebookPostNetwork.FACEBOOK_VIDEO,
      EditorialFacebookPostNetwork.TIKTOK,
      EditorialFacebookPostNetwork.MSN,
    ],
  },
  {
    label: 'Story',
    type: EditorialFacebookPostType.STORY,
    supportedNetworks: [EditorialFacebookPostNetwork.FACEBOOK],
  },
  {
    label: 'Text',
    type: EditorialFacebookPostType.TEXT,
    supportedNetworks: [
      EditorialFacebookPostNetwork.FACEBOOK,
      EditorialFacebookPostNetwork.BLUESKY,
      EditorialFacebookPostNetwork.TEXT_STATUS,
      EditorialFacebookPostNetwork.REDDIT,
    ],
  },
  {
    label: 'Image Album',
    type: EditorialFacebookPostType.IMAGE_ALBUM,
    supportedNetworks: [
      EditorialFacebookPostNetwork.FACEBOOK,
      EditorialFacebookPostNetwork.INSTAGRAM,
      // EditorialFacebookPostNetwork.TIKTOK,
    ],
  },
];

interface Props {
  onClose: VoidFunction;
  setNewPostType: React.Dispatch<
    React.SetStateAction<EditorialFacebookPostType | null>
  >;
}

const NewPostModal: FC<Props> = ({ onClose, setNewPostType }) => {
  const [network, networkLabel] = useNetwork();

  const onSelection = (type: EditorialFacebookPostType) => {
    setNewPostType(type);
    onClose();
  };

  const networkPostTypes = postTypes.filter((t) =>
    t.supportedNetworks.includes(network),
  );

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Heading $type="heading2">
          <NetworkLabel>{`${networkLabel} · `}</NetworkLabel>Post type
        </Heading>
        <Body>
          {networkPostTypes.map((postType) => (
            <Button
              key={postType.type}
              color="secondary"
              onClick={() => onSelection(postType.type)}
            >
              {postType.label}
            </Button>
          ))}

          <Gap>
            <Button
              color="secondary"
              onClick={() => onSelection(EditorialFacebookPostType.PLACEHOLDER)}
            >
              Time Gap Placeholder
            </Button>
          </Gap>
        </Body>
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
      </Dialog>
    </Modal>
  );
};

export default NewPostModal;
